import Css from "../../style.module.scss";

import { Checkbox } from "nlib/ui";
import { FormElement } from "nlib/common/Form";
import { getActiveOrganization } from "selectors/organizations";
import { getSelectedBusinessId } from "selectors/businesses";
import { getTextsData } from "selectors/texts";
import { getUserData } from "selectors/user";
import { useSelector } from "react-redux";
import Businesses from "utils/Businesses";
import DatePickerInput from "nlib/common/DatePickerInput";
import FormContext from "contexts/FormContext";
import FormLabel from "nlib/common/FormLabel";
import React, { useCallback, useContext, useMemo, useState } from "react";
import SelectAskPerson from "../AiBookkeeperTabProcessing/lib/SelectAskPerson";
import SelectDefaultAccountant from "../UncategorizedTab/lib/SelectDefaultAccountant";
import SelectRpaTransactionTypes from "./lib/SelectRpaTransactionTypes";
import UpsellCard from "./lib/UpsellCard";
import UpsellModal from "nlib/common/UpsellModal";
import UserRoles from "const/UserRoles";
import classNames from "classnames";

const AiBookkeeperTabGeneral = ({ businessId, businessData, hasBusinessUsers, onUpsellModalOpen, onUpsellModalClose }) => {
  const {
    values: { rpaStartFrom, rpaTransactionTypes, rpaAutoAskClient },
    onChange: onFormChange
  } = useContext(FormContext);

  const { uiTexts, messages } = useSelector(getTextsData);

  const { role: userRole } = useSelector(getUserData);

  const { businessOrganization } = useSelector(getActiveOrganization);

  const selectedBusinessId = useSelector(getSelectedBusinessId);

  const {
    createdAt,
    settings: {
      rpaStartFrom: currentSavedRpaStartFrom
    } = {},
    extraData: {
      integrationService,
      quickBooksBookCloseDate,
      quickBooksOldestTransactionDate,
      rpaUpsellDate,
      uncategorizedRpaEnabled
    } = {}
  } = businessData;

  const rpaStartFromMinDate = useMemo(() => {
    return Businesses.getRpaStartFromMinDate(
      integrationService,
      rpaUpsellDate,
      createdAt
    );
  }, [integrationService, rpaUpsellDate, createdAt]);

  const accountantUser = useMemo(() => {
    return UserRoles.checkIsAccountant(userRole);
  }, [userRole]);

  const upsellAvailable = useMemo(() => {
    return Businesses.checkUpsellAvailable({
      createdAt,
      integrationService,
      quickBooksOldestTransactionDate,
      quickBooksBookCloseDate,
      rpaStartFrom: rpaStartFrom || currentSavedRpaStartFrom,
      rpaUpsellDate,
      uncategorizedRpaEnabled
    });
  }, [
    createdAt,
    integrationService,
    quickBooksOldestTransactionDate,
    quickBooksBookCloseDate,
    rpaStartFrom,
    currentSavedRpaStartFrom,
    rpaUpsellDate,
    uncategorizedRpaEnabled
  ]);

  const [upsellModalShown, setUpsellModalShown] = useState(false);

  const handleRpaAutoAskClientToggleChange = useCallback((value) => {
    onFormChange({ name: "rpaAutoAskClient", value });
  }, [onFormChange]);

  const handleUpsellButtonClick = useCallback(() => {
    setUpsellModalShown(true);
    onUpsellModalOpen();
  }, [onUpsellModalOpen]);

  const handleUpsellModalClose = useCallback(() => {
    setUpsellModalShown(false);
    onUpsellModalClose();
  }, [onUpsellModalClose]);

  return (
    <>
      <div className={Css.row}>
        <div className={Css.col}>
          <FormLabel>{uiTexts.describeYourBusinessForAi}</FormLabel>
          <FormElement
            name="businessDescription"
            placeholder={messages.enterBusinessDescription} />
        </div>
      </div>
      <div className={Css.row}>
        <div className={Css.col}>
          <FormLabel>Type of transactions to process</FormLabel>
          <FormElement
            invalid={!rpaTransactionTypes}
            name="rpaTransactionTypes"
            placeholder={uiTexts.selectValue}
            element={SelectRpaTransactionTypes} />
        </div>
      </div>
      <div className={Css.row}>
        <div className={Css.col}>
          <FormLabel>{messages.onboardingRpaStartFrom}</FormLabel>
          <FormElement
            portal
            monthOnly
            name="rpaStartFrom"
            minDate={rpaStartFromMinDate}
            invalid={!rpaStartFrom}
            placeholder={uiTexts.selectDate}
            element={DatePickerInput} />
        </div>
      </div>
      {accountantUser && upsellAvailable && businessId === selectedBusinessId && (
        <div className={Css.row}>
          <div className={Css.col}>
            <UpsellCard onClick={handleUpsellButtonClick} />
          </div>
        </div>
      )}
      <div className={classNames(Css.row, rpaAutoAskClient && Css.rowWithRelatedContent)}>
        <div className={Css.col}>
          <Checkbox
            toggle
            checked={rpaAutoAskClient}
            onChange={handleRpaAutoAskClientToggleChange}>
            {messages.askClientAboutUnclearTransactionsToggleLabelText}
          </Checkbox>
          <div className={Css.description}>{messages.askClientAboutUnclearTransactionsToggleDescription}</div>
        </div>
      </div>
      {rpaAutoAskClient && (
        <div className={`${Css.row} ${Css.toggleRelatedContent}`}>
          <div className={Css.col}>
            <div className={Css.relatedInputBlock}>
              <div className={Css.relatedInputLabel}>{uiTexts.askPerson}</div>
              <FormElement
                businessId={businessId}
                name="rpaClientEmail"
                element={SelectAskPerson} />
            </div>
            {(!businessOrganization || hasBusinessUsers) && (
              <div className={Css.relatedInputBlock}>
                <div className={Css.relatedInputLabel}>{messages.accountantToAskClient}</div>
                <FormElement
                  businessId={businessId}
                  name="defaultClientAccountantEmail"
                  element={SelectDefaultAccountant} />
              </div>
            )}
          </div>
        </div>
      )}
      <div className={Css.row}>
        <div className={Css.col}>
          <FormElement
            toggle
            name="rpaEnabled"
            element={Checkbox}>{uiTexts.enableRoboticAi}</FormElement>
          <div className={Css.description}>{messages.enableRoboticAiToggleDescription}</div>
        </div>
      </div>
      {upsellModalShown && <UpsellModal onClose={handleUpsellModalClose} />}
    </>
  );
};

export default AiBookkeeperTabGeneral;
